import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingCart, User, Search } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

const Header = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const username = sessionStorage.getItem("username");
    setIsLoggedIn(!!token && !!username);
    if (token && username) fetchCartItems();
  }, []);

  const fetchCartItems = async () => {
    try {
      const token = localStorage.getItem("token");
      const userId = sessionStorage.getItem("userid");

      const response = await axios.get(`${API_URL}/api/cart/getusercart/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const products = response.data?.cart?.products;
      setCartItems(Array.isArray(products) ? products : []);
    } catch (error) {
      console.error("Error fetching cart items:", error);
      setCartItems([]);
    }
  };

  const handleSearch = (e) => setSearchQuery(e.target.value);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && searchQuery.trim()) {
      navigate(`/search?query=${encodeURIComponent(searchQuery.trim())}`);
      setShowMobileSearch(false); // hide after search
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    sessionStorage.removeItem("userid");
    sessionStorage.removeItem("username");
    setIsLoggedIn(false);
    setShowDropdown(false);
    navigate("/login");
  };

  return (
    <header className="w-full bg-white shadow-md fixed top-0 left-0 z-50">
      <nav className="flex justify-between items-center px-6 py-4 md:px-10 relative">
        <Link to="/" className="text-2xl font-bold text-green-600">PlantKart</Link>

        <ul className="hidden md:flex space-x-6">
          {[
            { name: "Home", path: "/" },
            { name: "Plants", path: "/plants" },
            { name: "Planters", path: "/planters" },
            { name: "Shop", path: "/shop" },
            { name: "Rent", path: "/rent" },
            { name: "Corporate Enquiry", path: "/enquiryform" }
          ].map(({ name, path }) => (
            <li key={name}>
              <Link to={path} className="text-gray-700 hover:text-green-600 transition">
                {name}
              </Link>
            </li>
          ))}
        </ul>

        <div className="flex items-center space-x-4">
          {/* Desktop Search */}
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            onKeyDown={handleKeyDown}
            placeholder="Search products..."
            className="hidden md:block px-3 py-1 border border-gray-300 rounded-lg"
          />

          {/* Mobile Search Icon */}
          <button
            className="md:hidden text-gray-600"
            onClick={() => setShowMobileSearch(!showMobileSearch)}
          >
            <Search size={24} />
          </button>

          <ShoppingCart
            className="text-gray-600 hover:text-green-600 cursor-pointer"
            onClick={() => navigate("/cart")}
          />

          {!isLoggedIn ? (
            <User
              className="text-gray-600 hover:text-green-600 cursor-pointer"
              onClick={() => navigate("/login")}
            />
          ) : (
            <div className="relative">
              <User
                className="text-gray-600 hover:text-green-600 cursor-pointer"
                onClick={() => setShowDropdown(!showDropdown)}
              />
              {showDropdown && (
                <div className="absolute right-0 mt-2 w-40 bg-white shadow rounded z-50">
                  <button onClick={() => { navigate("/profile"); setShowDropdown(false); }} className="block w-full text-left px-4 py-2 hover:bg-gray-100">My Profile</button>
                  <button onClick={() => { navigate("/myorders"); setShowDropdown(false); }} className="block w-full text-left px-4 py-2 hover:bg-gray-100">My Orders</button>
                  <button onClick={logout} className="block w-full text-left px-4 py-2 text-red-500 hover:bg-gray-100">Logout</button>
                </div>
              )}
            </div>
          )}

          {/* Hamburger */}
          <button className="md:hidden text-gray-700" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </nav>

      {/* Mobile Search Input */}
      {showMobileSearch && (
        <div className="md:hidden px-4 pb-3 bg-white shadow">
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            onKeyDown={handleKeyDown}
            placeholder="Search products..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            autoFocus
          />
        </div>
      )}

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden flex flex-col items-center bg-white py-4 shadow-md">
          {[
            { name: "Home", path: "/" },
            { name: "Plants", path: "/plants" },
            { name: "Planters", path: "/planters" },
            { name: "Shop", path: "/shop" },
            { name: "Rent", path: "/rent" },
            { name: "Corporate Enquiry", path: "/enquiryform" }
          ].map(({ name, path }) => (
            <Link
              key={name}
              to={path}
              className="py-2 text-gray-700 hover:text-green-600"
              onClick={() => setMenuOpen(false)}
            >
              {name}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
};

export default Header;
